import React, { useState, useMemo } from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  FileTextIcon,
  ShieldIcon,
  CreditCardIcon,
  AlertTriangleIcon,
  LockIcon,
  UsersIcon,
  SearchIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FilterIcon,
  ScaleIcon,
  BellIcon,
  GlobeIcon,
  XIcon
} from 'lucide-react';

const UltraTermsOfService = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedSections, setExpandedSections] = useState({});

  // Define categories
  const categories = [
    { id: 'all', name: 'Tüm Maddeler', icon: FileTextIcon, color: 'bg-gray-100 text-gray-700' },
    { id: 'general', name: 'Genel Koşullar', icon: FileTextIcon, color: 'bg-blue-100 text-blue-700' },
    { id: 'privacy', name: 'Gizlilik ve Veri Koruma', icon: LockIcon, color: 'bg-purple-100 text-purple-700' },
    { id: 'usage', name: 'Kullanım Koşulları', icon: UsersIcon, color: 'bg-green-100 text-green-700' },
    { id: 'payment', name: 'Ödeme ve Faturalandırma', icon: CreditCardIcon, color: 'bg-yellow-100 text-yellow-700' },
    { id: 'liability', name: 'Sorumluluk ve Garanti', icon: ShieldIcon, color: 'bg-red-100 text-red-700' },
    { id: 'termination', name: 'İptal ve Sonlandırma', icon: AlertTriangleIcon, color: 'bg-orange-100 text-orange-700' },
    { id: 'legal', name: 'Yasal Hükümler', icon: ScaleIcon, color: 'bg-indigo-100 text-indigo-700' },
  ];

  // Define all terms sections with content
  const allTerms = [
    // General Terms
    {
      id: 'term-general-1',
      category: 'general',
      title: '1. Sözleşmenin Tarafları ve Konusu',
      description: 'Bu kullanıcı sözleşmesi, Advancecash.io platformunu kullanan tüm kullanıcıları bağlar.',
      icon: FileTextIcon,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-l-blue-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <p><strong>1.1 Taraflar:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Hizmet Sağlayıcı:</strong> Advancecash.io (bundan sonra "Platform" olarak anılacaktır)</li>
            <li><strong>Kullanıcı:</strong> Platformu kullanan gerçek veya tüzel kişiler</li>
          </ul>
          
          <p><strong>1.2 Sözleşmenin Konusu:</strong></p>
          <p>Bu sözleşme, Platform tarafından sunulan çalışan avans yönetim hizmetlerinin kullanım şartlarını, tarafların hak ve yükümlülüklerini düzenler.</p>
          
          <p><strong>1.3 Sözleşmenin Kabulü:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Kullanıcı, platforma kayıt olarak bu sözleşmeyi kabul etmiş sayılır</li>
            <li>Sözleşme şartlarını kabul etmeden platform kullanılamaz</li>
            <li>Platform, sözleşme şartlarını önceden bildirimde bulunarak değiştirme hakkını saklı tutar</li>
          </ul>

          <p><strong>1.4 Geçerlilik:</strong></p>
          <p>Bu sözleşme, kullanıcının hesabı aktif olduğu sürece geçerlidir ve tarafları bağlar.</p>
        </div>
      )
    },
    {
      id: 'term-general-2',
      category: 'general',
      title: '2. Tanımlar',
      description: 'Sözleşmede kullanılan temel terimlerin açıklamaları.',
      icon: FileTextIcon,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-l-blue-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Platform:</strong> Advancecash.io web uygulaması ve tüm alt servisleri</p>
          <p><strong>Kullanıcı:</strong> Platforma kayıt olan ve hizmetleri kullanan gerçek veya tüzel kişiler</p>
          <p><strong>Tenant/Kiracı:</strong> Platform üzerinde kayıtlı her bir şirket hesabı</p>
          <p><strong>Avans:</strong> Çalışanlara verilen geçici ön ödeme</p>
          <p><strong>Ultra Admin:</strong> Platform yöneticisi, tüm tenant'ları yöneten yetkili</p>
          <p><strong>Abonelik:</strong> Ücretli hizmet paketleri (Starter, Professional, Enterprise)</p>
          <p><strong>Deneme Süresi:</strong> Ücretsiz olarak sunulan sınırlı kullanım süresi</p>
          <p><strong>Kişisel Veri:</strong> Kullanıcıya ait kimlik, iletişim ve kullanım bilgileri</p>
        </div>
      )
    },

    // Privacy & Data Protection
    {
      id: 'term-privacy-1',
      category: 'privacy',
      title: '3. Kişisel Verilerin İşlenmesi',
      description: 'KVKK ve GDPR uyumlu veri işleme politikası.',
      icon: LockIcon,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-l-purple-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <p><strong>3.1 Toplanan Veriler:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Kimlik bilgileri (ad, soyad, e-posta, telefon)</li>
            <li>Şirket bilgileri (şirket adı, vergi numarası)</li>
            <li>Kullanım verileri (giriş zamanları, IP adresi)</li>
            <li>Avans talep ve onay kayıtları</li>
            <li>Ödeme ve fatura bilgileri</li>
          </ul>
          
          <p><strong>3.2 Veri İşleme Amaçları:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Hizmet sunumu ve platform işleyişi</li>
            <li>Kullanıcı kimlik doğrulama ve güvenlik</li>
            <li>Faturalandırma ve muhasebe işlemleri</li>
            <li>Yasal yükümlülüklerin yerine getirilmesi</li>
            <li>Platform iyileştirme ve analiz</li>
          </ul>

          <p><strong>3.3 Veri Güvenliği:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Tüm veriler şifreli olarak saklanır (AES-256)</li>
            <li>Şifreler hash'lenerek (SHA-256) güvenli tutulur</li>
            <li>Düzenli güvenlik testleri yapılır</li>
            <li>Yetkilendirilmiş personel dışında erişim engellenir</li>
          </ul>

          <p><strong>3.4 Kullanıcı Hakları:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Verilerinize erişim hakkı</li>
            <li>Düzeltme ve güncelleme hakkı</li>
            <li>Silme hakkı (hesap kapatma)</li>
            <li>Veri taşınabilirliği hakkı</li>
            <li>İşleme itiraz hakkı</li>
          </ul>
        </div>
      )
    },
    {
      id: 'term-privacy-2',
      category: 'privacy',
      title: '4. Veri Saklama ve Silme',
      description: 'Kişisel verilerin saklanma süreleri ve silme prosedürleri.',
      icon: ShieldIcon,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-l-purple-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <p><strong>4.1 Saklama Süreleri:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Aktif Hesap Verileri:</strong> Hesap aktif olduğu sürece</li>
            <li><strong>Finansal Kayıtlar:</strong> Yasal saklama süresi (10 yıl)</li>
            <li><strong>Log Kayıtları:</strong> 6 ay</li>
            <li><strong>Pazarlama İzni:</strong> İzin geri alınana kadar</li>
          </ul>
          
          <p><strong>4.2 Hesap Silme:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Kullanıcı, hesabını güvenlik ayarlarından kalıcı olarak silebilir</li>
            <li>Silme işlemi geri alınamaz</li>
            <li>Yasal saklama yükümlülüğü olan veriler (faturalar) hariç tüm veriler silinir</li>
            <li>30 gün içinde veriler sistemden tamamen kaldırılır</li>
          </ul>

          <p><strong>4.3 Şirket Hesabı Silme:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Şirket admini, şirket hesabını ve tüm kullanıcıları silebilir</li>
            <li>Tüm avans kayıtları ve raporlar silinir</li>
            <li>Finansal kayıtlar yasal süre kadar arşivde tutulur</li>
            <li>Silme işlemi öncesi kullanıcılara bildirim yapılır</li>
          </ul>
        </div>
      )
    },

    // Usage Terms
    {
      id: 'term-usage-1',
      category: 'usage',
      title: '5. Kullanıcı Yükümlülükleri',
      description: 'Platformu kullanırken uyulması gereken kurallar ve sorumluluklar.',
      icon: UsersIcon,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-l-green-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <p><strong>5.1 Genel Yükümlülükler:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Kayıt sırasında doğru ve güncel bilgi vermek</li>
            <li>Hesap güvenliğini sağlamak (güçlü şifre kullanmak)</li>
            <li>Hesap bilgilerini üçüncü kişilerle paylaşmamak</li>
            <li>Platform'u yasal amaçlar için kullanmak</li>
            <li>Diğer kullanıcıların haklarına saygı göstermek</li>
          </ul>
          
          <p><strong>5.2 Yasaklı Faaliyetler:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Platformu yasadışı amaçlarla kullanmak</li>
            <li>Sahte hesap oluşturmak</li>
            <li>Sistem güvenliğini tehdit eden işlemler yapmak</li>
            <li>Spam veya zararlı içerik göndermek</li>
            <li>Diğer kullanıcıların verilerini çalmaya çalışmak</li>
            <li>Platform'u tersine mühendislik ile analiz etmek</li>
          </ul>

          <p><strong>5.3 Şirket Admini Sorumlulukları:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Kullanıcı yetkilendirmelerini doğru yapmak</li>
            <li>Avans kurallarını şirket politikasına uygun belirlemek</li>
            <li>Çalışanların platform kullanımını denetlemek</li>
            <li>Ödeme ve abonelik yükümlülüklerini yerine getirmek</li>
          </ul>

          <p><strong>5.4 İhlal Durumu:</strong></p>
          <p>Kullanıcı yükümlülüklerini ihlal etmesi durumunda Platform, kullanıcı hesabını askıya alabilir veya kalıcı olarak sonlandırabilir.</p>
        </div>
      )
    },
    {
      id: 'term-usage-2',
      category: 'usage',
      title: '6. Hizmet Kapsamı ve Sınırlamaları',
      description: 'Platform tarafından sunulan hizmetler ve kullanım limitleri.',
      icon: GlobeIcon,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-l-green-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <p><strong>6.1 Sunulan Hizmetler:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Çalışan avans talep yönetimi</li>
            <li>Çok seviyeli onay sistemi</li>
            <li>Avans kapama ve harcama takibi</li>
            <li>Cari hesap ve raporlama</li>
            <li>Kullanıcı ve yetki yönetimi</li>
            <li>Şirket ayarları ve özelleştirme</li>
          </ul>
          
          <p><strong>6.2 Paket Sınırlamaları:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Deneme Süresi:</strong> 14 gün, 5 kullanıcı</li>
            <li><strong>Starter:</strong> 5 kullanıcı, temel özellikler</li>
            <li><strong>Professional:</strong> 15 kullanıcı, gelişmiş raporlama</li>
            <li><strong>Enterprise:</strong> 50 kullanıcı, özel destek</li>
          </ul>

          <p><strong>6.3 Hizmet Kesintileri:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Platform, planlı bakım için hizmeti geçici olarak durdurabilir</li>
            <li>Bakım öncesinde kullanıcılara bildirim yapılır</li>
            <li>Beklenmedik kesintiler en kısa sürede giderilir</li>
            <li>Kritik güvenlik güncellemeleri acil olarak uygulanabilir</li>
          </ul>

          <p><strong>6.4 Kullanım Koşulları:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Platform 7/24 erişilebilir olmaya çalışır</li>
            <li>%99.5 uptime hedefi vardır</li>
            <li>Teknik destek 09:00-18:00 arası aktiftir</li>
            <li>Enterprise müşterilere öncelikli destek verilir</li>
          </ul>
        </div>
      )
    },

    // Payment Terms
    {
      id: 'term-payment-1',
      category: 'payment',
      title: '7. Ödeme Koşulları',
      description: 'Abonelik ücretleri, ödeme yöntemleri ve faturalandırma.',
      icon: CreditCardIcon,
      iconColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-l-yellow-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <p><strong>7.1 Fiyatlandırma:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Deneme Süresi:</strong> İlk 14 gün ücretsiz</li>
            <li><strong>Starter Paketi:</strong> Aylık ücret uygulanır</li>
            <li><strong>Professional Paketi:</strong> Aylık ücret uygulanır</li>
            <li><strong>Enterprise Paketi:</strong> Aylık ücret uygulanır</li>
            <li>Tüm fiyatlar KDV hariçtir</li>
          </ul>
          
          <p><strong>7.2 Ödeme Yöntemi:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Kredi kartı (Visa, Mastercard, American Express)</li>
            <li>Banka kartı</li>
            <li>Otomatik yenileme (varsayılan)</li>
            <li>Manuel ödeme seçeneği</li>
          </ul>

          <p><strong>7.3 Faturalandırma:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Faturalar aylık dönemler halinde kesilir</li>
            <li>E-fatura sistemi üzerinden gönderilir</li>
            <li>Her ay ilk gününde otomatik tahsilat yapılır</li>
            <li>Ödeme başarısız olursa 3 gün içinde tekrar denenir</li>
            <li>Ödeme yapılmazsa hesap askıya alınır</li>
          </ul>

          <p><strong>7.4 Fiyat Değişiklikleri:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Platform, fiyatları değiştirme hakkını saklı tutar</li>
            <li>Değişiklikler 30 gün önceden bildirilir</li>
            <li>Mevcut abonelikler dönem sonuna kadar eski fiyattan devam eder</li>
            <li>Kullanıcı değişiklikleri kabul etmezse aboneliğini iptal edebilir</li>
          </ul>
        </div>
      )
    },
    {
      id: 'term-payment-2',
      category: 'payment',
      title: '8. İade ve İptal Politikası',
      description: 'Ödeme iadesi, abonelik iptali ve para iadesi koşulları.',
      icon: AlertTriangleIcon,
      iconColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-l-yellow-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <p><strong>8.1 İade Politikası:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Deneme süresi içinde iptal edilirse ücret tahsil edilmez</li>
            <li>Aylık abonelikler için iade yapılmaz</li>
            <li>Hizmet kesintisi 24 saatten fazla sürerse orantılı iade yapılır</li>
            <li>Teknik sorunlar nedeniyle kullanılamazlık durumunda indirim uygulanabilir</li>
          </ul>
          
          <p><strong>8.2 Abonelik İptali:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Kullanıcı istediği zaman aboneliğini iptal edebilir</li>
            <li>İptal, mevcut dönem sonunda etkili olur</li>
            <li>İptal sonrası kalan günler için ücret iadesi yapılmaz</li>
            <li>İptal edilen hesap sonraki dönemde otomatik olarak askıya alınır</li>
          </ul>

          <p><strong>8.3 Paket Değişikliği:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Yükseltme (Upgrade):</strong> Hemen etkili olur, fark tutarı tahsil edilir</li>
            <li><strong>Düşürme (Downgrade):</strong> Dönem sonunda etkili olur, iade yapılmaz</li>
            <li>Paket değişikliği her dönemde sadece 1 kez yapılabilir</li>
          </ul>

          <p><strong>8.4 Hesap Kapatma:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Hesap kapatma işlemi kalıcıdır ve geri alınamaz</li>
            <li>Tüm veriler 30 gün içinde silinir</li>
            <li>Kalan abonelik süresi için iade yapılmaz</li>
            <li>Kullanılmayan krediler veya bakiyeler iade edilmez</li>
          </ul>
        </div>
      )
    },

    // Liability Terms
    {
      id: 'term-liability-1',
      category: 'liability',
      title: '9. Sorumluluk Sınırlamaları',
      description: 'Platform ve kullanıcı sorumlulukları, garanti ve tazminat.',
      icon: ShieldIcon,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-l-red-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <p><strong>9.1 Platform Sorumlulukları:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Hizmetin kesintisiz ve hatasız sunulması için çaba gösterir</li>
            <li>Kullanıcı verilerinin güvenliğini sağlar</li>
            <li>KVKK ve GDPR düzenlemelerine uygun hareket eder</li>
            <li>Teknik destek hizmeti sunar</li>
          </ul>
          
          <p><strong>9.2 Platform Sorumlu Değildir:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Kullanıcının hatalı veri girmesinden kaynaklanan sorunlar</li>
            <li>Kullanıcının hesap bilgilerini paylaşmasından doğan zararlar</li>
            <li>İnternet bağlantısı veya üçüncü parti hizmet kesintileri</li>
            <li>Kullanıcıların kendi aralarındaki anlaşmazlıklar</li>
            <li>Platform dışında yapılan işlemler</li>
            <li>Force majeure durumları (doğal afetler, savaş, vs.)</li>
          </ul>

          <p><strong>9.3 Tazminat Sınırı:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Platform sorumluluğu, son 12 ayda ödenen abonelik ücreti ile sınırlıdır</li>
            <li>Dolaylı zararlar (kar kaybı, iş kaybı) tazmin edilmez</li>
            <li>Veri kaybı durumunda yedekleme sorumluluğu kullanıcıya aittir</li>
          </ul>

          <p><strong>9.4 Kullanıcı Tazminat Yükümlülüğü:</strong></p>
          <p>Kullanıcı, platform kurallarını ihlal ederek Platform'a, diğer kullanıcılara veya üçüncü kişilere verdiği zararlardan sorumludur ve bu zararları tazmin etmekle yükümlüdür.</p>
        </div>
      )
    },
    {
      id: 'term-liability-2',
      category: 'liability',
      title: '10. Garanti ve Feragatler',
      description: 'Hizmet garantileri ve yasal feragatnameler.',
      icon: AlertTriangleIcon,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-l-red-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <p><strong>10.1 Hizmet Garantisi:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Platform, hizmetleri "olduğu gibi" ve "mevcut olduğu şekliyle" sunar</li>
            <li>Kesintisiz ve hatasız çalışma garantisi verilmez</li>
            <li>%99.5 uptime hedefi vardır ancak garanti edilmez</li>
            <li>Platform, özellik ekleyebilir veya kaldırabilir</li>
          </ul>
          
          <p><strong>10.2 Veri Güvenliği Garantisi:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Endüstri standardı güvenlik önlemleri alınır</li>
            <li>Tüm veriler şifrelenir (SSL/TLS, AES-256)</li>
            <li>%100 güvenlik garanti edilemez</li>
            <li>Kullanıcılar kendi yedeklerini almalıdır</li>
          </ul>

          <p><strong>10.3 Yasal Uyumluluk Feragati:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Platform, yerel vergi ve iş kanunlarına uyum sağlamaz</li>
            <li>Avans yönetimi yasal düzenlemelere uygun olma sorumluluğu şirkete aittir</li>
            <li>Muhasebe kayıtlarının yasal geçerliliği garanti edilmez</li>
            <li>Mali müşavir onayı alınması önerilir</li>
          </ul>

          <p><strong>10.4 Üçüncü Parti Hizmetler:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Ödeme işlemleri Stripe üzerinden yapılır (güvenlik Stripe sorumluluğundadır)</li>
            <li>E-posta gönderimi SendGrid kullanır</li>
            <li>Platform, üçüncü parti hizmetlerin kesintilerinden sorumlu değildir</li>
          </ul>
        </div>
      )
    },

    // Termination
    {
      id: 'term-termination-1',
      category: 'termination',
      title: '11. Sözleşmenin Sona Ermesi',
      description: 'Hesap kapatma, abonelik iptali ve sözleşme feshi.',
      icon: AlertTriangleIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-l-orange-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <p><strong>11.1 Kullanıcı Tarafından Sonlandırma:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Kullanıcı istediği zaman hesabını kapatabilir</li>
            <li>Güvenlik Ayarları üzerinden "Hesabımı Sil" işlemi yapılır</li>
            <li>İşlem geri alınamaz ve tüm veriler silinir</li>
            <li>Kalan abonelik süresi için iade yapılmaz</li>
          </ul>
          
          <p><strong>11.2 Şirket Hesabı Sonlandırma:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Şirket admini, şirket hesabını tamamen kapatabilir</li>
            <li>Tüm kullanıcılar ve veriler silinir</li>
            <li>30 günlük silme süreci başlatılır</li>
            <li>Finansal kayıtlar yasal süre boyunca arşivde tutulur</li>
          </ul>

          <p><strong>11.3 Platform Tarafından Sonlandırma:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Ödeme yapılmaması (7 günlük ek süre verilir)</li>
            <li>Kullanım şartlarının ihlali</li>
            <li>Yasadışı faaliyet tespiti</li>
            <li>Diğer kullanıcılara zarar verme</li>
            <li>Sahte hesap veya bilgi kullanımı</li>
          </ul>

          <p><strong>11.4 Sonlandırma Sonrası:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Hesap erişimi anında kapatılır</li>
            <li>Tüm veriler 30 gün içinde silinir</li>
            <li>Yasal saklama yükümlülüğü olan belgeler hariç</li>
            <li>Yeni hesap açmak için tekrar kayıt gerekir</li>
          </ul>
        </div>
      )
    },

    // Legal
    {
      id: 'term-legal-1',
      category: 'legal',
      title: '12. Fikri Mülkiyet Hakları',
      description: 'Telif hakları, marka hakları ve fikri mülkiyet koruması.',
      icon: ScaleIcon,
      iconColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-l-indigo-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <p><strong>12.1 Platform Mülkiyeti:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Advancecash.io markası ve logosu tescillidir</li>
            <li>Tüm yazılım kodları Platform'a aittir</li>
            <li>Tasarım, arayüz ve kullanıcı deneyimi korunmaktadır</li>
            <li>Dokümantasyon ve eğitim materyalleri telif koruması altındadır</li>
          </ul>
          
          <p><strong>12.2 Kullanıcı İçeriği:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Kullanıcı tarafından girilen veriler kullanıcıya aittir</li>
            <li>Platform, kullanıcı verilerini hizmet sunumu için kullanabilir</li>
            <li>Kullanıcı verileri anonim hale getirilerek analiz yapılabilir</li>
            <li>Veriler üçüncü kişilerle paylaşılmaz (yasal zorunluluk hariç)</li>
          </ul>

          <p><strong>12.3 Lisans Hakkı:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Kullanıcıya sınırlı, devredilemez kullanım lisansı verilir</li>
            <li>Kaynak kodlara erişim ve değişiklik yasaktır</li>
            <li>Tersine mühendislik yapılamaz</li>
            <li>Platform'un kopyalanması veya dağıtımı yasaktır</li>
          </ul>

          <p><strong>12.4 İhlal Durumu:</strong></p>
          <p>Fikri mülkiyet haklarının ihlali durumunda yasal yollara başvurulur ve tazminat talep edilir.</p>
        </div>
      )
    },
    {
      id: 'term-legal-2',
      category: 'legal',
      title: '13. Uyuşmazlık Çözümü ve Yetki',
      description: 'Anlaşmazlıkların çözümü, yargı yetkisi ve uygulanacak hukuk.',
      icon: ScaleIcon,
      iconColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-l-indigo-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <p><strong>13.1 Uygulanacak Hukuk:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Bu sözleşmeye Türkiye Cumhuriyeti kanunları uygulanır</li>
            <li>KVKK (Kişisel Verilerin Korunması Kanunu) ve GDPR'a uyulur</li>
            <li>E-Ticaret Kanunu ve Tüketicinin Korunması Hakkında Kanun geçerlidir</li>
          </ul>
          
          <p><strong>13.2 Yetki:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Anlaşmazlıkların çözümünde İstanbul Mahkemeleri ve İcra Daireleri yetkilidir</li>
            <li>Tüketici sözleşmelerinde Tüketici Hakem Heyetleri yetkilidir</li>
            <li>5000 TL altındaki anlaşmazlıklarda Tüketici Hakem Heyeti zorunludur</li>
          </ul>

          <p><strong>13.3 Uyuşmazlık Çözüm Süreci:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>1. Müzakere:</strong> Taraflar dostane çözüm için görüşür (30 gün)</li>
            <li><strong>2. Arabuluculuk:</strong> Gerekirse arabulucu atanır (60 gün)</li>
            <li><strong>3. Yargı Yolu:</strong> Çözüm sağlanamazsa mahkemeye başvurulur</li>
          </ul>

          <p><strong>13.4 Delil Sözleşmesi:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Platform kayıtları, log dosyaları ve e-postalar delil sayılır</li>
            <li>Elektronik kayıtlar Hukuk Muhakemeleri Kanunu uyarınca geçerlidir</li>
            <li>Taraflar, 6100 Sayılı HMK m. 193 kapsamında elektronik kayıtları delil olarak kabul eder</li>
          </ul>
        </div>
      )
    },
    {
      id: 'term-legal-3',
      category: 'legal',
      title: '14. Sözleşme Değişiklikleri ve Güncellemeler',
      description: 'Kullanıcı sözleşmesinin güncellenmesi ve bildirim prosedürleri.',
      icon: BellIcon,
      iconColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-l-indigo-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <p><strong>14.1 Değişiklik Hakkı:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Platform, sözleşme şartlarını tek taraflı değiştirme hakkına sahiptir</li>
            <li>Değişiklikler yasal düzenlemelere uyum için gerekli olabilir</li>
            <li>Hizmet iyileştirmeleri değişiklik gerektirebilir</li>
          </ul>
          
          <p><strong>14.2 Bildirim:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Değişiklikler 30 gün önceden kullanıcılara bildirilir</li>
            <li>E-posta ve platform içi bildirim gönderilir</li>
            <li>Önemli değişiklikler login ekranında vurgulanır</li>
            <li>Güncel sözleşme metni her zaman platformda yayınlanır</li>
          </ul>

          <p><strong>14.3 Kabul ve Red:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Bildirimin ardından platform kullanımı değişiklikleri kabul sayılır</li>
            <li>Kullanıcı değişiklikleri kabul etmezse hesabını kapatabilir</li>
            <li>30 gün içinde itiraz edilmezse değişiklikler kabul edilmiş sayılır</li>
            <li>Fiyat değişikliklerinde mevcut dönem eski fiyattan devam eder</li>
          </ul>

          <p><strong>14.4 Versiyonlama:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Her güncelleme yeni versiyon numarası alır</li>
            <li>Eski versiyonlar arşivde saklanır</li>
            <li>Kullanıcı hangi versiyonu kabul ettiği kayıt altına alınır</li>
          </ul>

          <p className="mt-4 text-xs text-gray-500">
            <strong>Son Güncelleme:</strong> 15 Ocak 2025<br />
            <strong>Versiyon:</strong> 1.0
          </p>
        </div>
      )
    },
  ];

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Expand all in category
  const expandAllInCategory = (categoryId) => {
    const categoryTerms = filteredTerms.filter(t => t.category === categoryId);
    const newExpanded = { ...expandedSections };
    categoryTerms.forEach(term => {
      newExpanded[term.id] = true;
    });
    setExpandedSections(newExpanded);
  };

  // Collapse all
  const collapseAll = () => {
    setExpandedSections({});
  };

  // Filter and search logic
  const filteredTerms = useMemo(() => {
    let filtered = allTerms;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(term => term.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.title.toLowerCase().includes(term) ||
        item.description.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [selectedCategory, searchTerm]);

  // Get stats
  const stats = {
    total: allTerms.length,
    categories: categories.length - 1, // Exclude 'all'
    byCategory: categories.slice(1).map(cat => ({
      ...cat,
      count: allTerms.filter(t => t.category === cat.id).length
    }))
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <FileTextIcon className="w-8 h-8 mr-3 text-blue-600" />
              Kullanıcı Sözleşmesi
            </h2>
            <p className="text-gray-600 mt-1">Advancecash.io Platform Kullanım Şartları ve Koşulları</p>
          </div>
          <div className="flex space-x-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-xs text-gray-500">Toplam Madde</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">{stats.categories}</div>
              <div className="text-xs text-gray-500">Kategori</div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Sözleşmede ara... (örn: gizlilik, ödeme, iptal)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-12 py-3 w-full text-lg"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar - Category Filter */}
        <div className="col-span-3">
          <Card className="p-4 sticky top-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center">
                <FilterIcon className="w-4 h-4 mr-2" />
                Kategoriler
              </h3>
              <button
                onClick={collapseAll}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Tümünü Kapat
              </button>
            </div>
            <div className="space-y-2">
              {categories.map((category) => {
                const Icon = category.icon;
                const count = category.id === 'all' 
                  ? stats.total 
                  : stats.byCategory.find(c => c.id === category.id)?.count || 0;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                      selectedCategory === category.id
                        ? 'bg-blue-100 border-2 border-blue-500'
                        : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg ${category.color} flex items-center justify-center`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{category.name}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">{count}</Badge>
                  </button>
                );
              })}
            </div>

            {selectedCategory !== 'all' && (
              <button
                onClick={() => expandAllInCategory(selectedCategory)}
                className="w-full mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Tümünü Genişlet
              </button>
            )}
          </Card>
        </div>

        {/* Main Content - Terms List */}
        <div className="col-span-9">
          {filteredTerms.length === 0 ? (
            <Card className="p-12 text-center">
              <SearchIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Madde Bulunamadı</h3>
              <p className="text-gray-600">
                {searchTerm ? `"${searchTerm}" için sonuç bulunamadı.` : 'Bu kategoride madde bulunmuyor.'}
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredTerms.map((term) => {
                const Icon = term.icon;
                const isExpanded = expandedSections[term.id];

                return (
                  <Card key={term.id} className={`border-l-4 ${term.borderColor} ${term.bgColor}`}>
                    <div
                      className="p-4 cursor-pointer"
                      onClick={() => toggleSection(term.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className={`w-10 h-10 rounded-lg ${term.bgColor} flex items-center justify-center flex-shrink-0`}>
                            <Icon className={`w-5 h-5 ${term.iconColor}`} />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">{term.title}</h4>
                            <p className="text-sm text-gray-600">{term.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <Badge className={categories.find(c => c.id === term.category)?.color}>
                            {categories.find(c => c.id === term.category)?.name}
                          </Badge>
                          {isExpanded ? (
                            <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-4 pt-2 border-t border-gray-200">
                        <div className="ml-14">
                          {term.content}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UltraTermsOfService;
